import React from 'react';
import { useTranslation } from 'react-i18next';
import { LogIn, UserPlus, User } from 'lucide-react';

interface AuthOptionsViewProps {
  onSelectOption: (option: 'login' | 'create' | 'guest') => void;
  emailExists?: boolean;
  phoneExists?: boolean;
  contactInfo?: { email?: string; phone?: string };
  lookupStatus?: 'unknown' | 'not_found' | 'found' | 'create_new';
  /** When true, the guest booking option is hidden (e.g. for tattoo, where an account is always required) */
  hideGuestOption?: boolean;
  /** When true, the third option is labeled as anonymous (piercing) */
  anonymousGuest?: boolean;
}

export function AuthOptionsView({
  onSelectOption,
  emailExists,
  phoneExists,
  contactInfo,
  lookupStatus = 'unknown',
  hideGuestOption = false,
  anonymousGuest = false,
}: AuthOptionsViewProps) {
  const { t } = useTranslation();
  const hasContactInfo = !!(contactInfo?.email || contactInfo?.phone);
  const showNotFoundMessage = lookupStatus === 'not_found' && hasContactInfo;
  const showCreateNewMessage = lookupStatus === 'create_new' && hasContactInfo;

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold mb-2">{t('customerDetails.title')}</h2>
        <p className="text-gray-600">{t('customerDetails.subtitle')}</p>
      </div>

      {hasContactInfo && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-sm text-blue-800 font-medium">
            {t('customerDetails.contactProvided.title')}
          </p>
          <div className="text-sm text-blue-700 mt-1 space-y-1">
            {contactInfo?.email && (
              <div>{t('customerDetails.contactProvided.email', { email: contactInfo.email })}</div>
            )}
            {contactInfo?.phone && (
              <div>{t('customerDetails.contactProvided.phone', { phone: contactInfo.phone })}</div>
            )}
          </div>
        </div>
      )}

      {(showNotFoundMessage || showCreateNewMessage) && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <p className="text-sm text-yellow-800 font-medium">
            {showNotFoundMessage
              ? t('customerDetails.lookup.notFoundTitle')
              : t('customerDetails.lookup.createNewTitle')}
          </p>
          <p className="text-sm text-yellow-700 mt-1">
            {showNotFoundMessage
              ? t('customerDetails.lookup.notFoundDescription')
              : t('customerDetails.lookup.createNewDescription')}
          </p>
        </div>
      )}

      {/* Warning if email/phone exists */}
      {(emailExists || phoneExists) && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
          <p className="text-sm text-yellow-800">
            {emailExists && phoneExists
              ? t('customerDetails.warning.bothExist')
              : emailExists
              ? t('customerDetails.warning.emailExists')
              : t('customerDetails.warning.phoneExists')}
          </p>
        </div>
      )}

      <div className={`grid grid-cols-1 ${hideGuestOption ? 'md:grid-cols-2' : 'md:grid-cols-3'} gap-4`}>
        {/* Option 1: Login */}
        <button
          onClick={() => onSelectOption('login')}
          className="p-6 border-2 border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors text-left"
        >
          <div className="flex items-center space-x-3 mb-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <LogIn className="w-6 h-6 text-blue-600" />
            </div>
            <h3 className="font-semibold text-lg">{t('customerDetails.options.login.title')}</h3>
          </div>
          <p className="text-sm text-gray-600">{t('customerDetails.options.login.description')}</p>
        </button>

        {/* Option 2: Create Account */}
        <button
          onClick={() => onSelectOption('create')}
          className="p-6 border-2 border-gray-200 rounded-lg hover:border-green-500 hover:bg-green-50 transition-colors text-left"
        >
          <div className="flex items-center space-x-3 mb-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <UserPlus className="w-6 h-6 text-green-600" />
            </div>
            <h3 className="font-semibold text-lg">{t('customerDetails.options.create.title')}</h3>
          </div>
          <p className="text-sm text-gray-600">{t('customerDetails.options.create.description')}</p>
        </button>

        {/* Option 3: Guest Booking (hidden when hideGuestOption is true, e.g. for tattoo) */}
        {!hideGuestOption && (
          <button
            onClick={() => onSelectOption('guest')}
            className="p-6 border-2 border-gray-200 rounded-lg hover:border-purple-500 hover:bg-purple-50 transition-colors text-left"
          >
            <div className="flex items-center space-x-3 mb-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <User className="w-6 h-6 text-purple-600" />
              </div>
            <h3 className="font-semibold text-lg">
              {anonymousGuest
                ? t('customerDetails.options.anonymous.title')
                : t('customerDetails.options.guest.title')}
            </h3>
          </div>
          <p className="text-sm text-gray-600">
            {anonymousGuest
              ? t('customerDetails.options.anonymous.description')
              : t('customerDetails.options.guest.description')}
          </p>
          </button>
        )}
      </div>
    </div>
  );
}

